import { useState, useEffect } from "react";
import SearchBar from "@/components/SearchBar";
import ToolCard from "@/components/ToolCard";
import SubmitToolDialog from "@/components/SubmitToolDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import type { Tool } from "@/data/tools";
import MetaTags from "@/components/MetaTags";
import { startOfWeek, endOfWeek, subWeeks, format } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import CategoryFilter from "@/components/CategoryFilter";
import SportsFilter from "@/components/SportsFilter";
import PlatformFilter from "@/components/PlatformFilter";
import usePageTitle from '../hooks/usePageTitle';

const Index = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [toolsByWeek, setToolsByWeek] = useState<{
    period: string;
    startDate: Date;
    endDate: Date;
    tools: Tool[];
  }[]>([]);
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [sports, setSports] = useState<{id: string, name: string}[]>([]);
  const [platforms, setPlatforms] = useState<{id: string, name: string}[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Set the page title
  usePageTitle('Home');

  useEffect(() => {
    loadTools();
    loadFilters();
  }, [selectedCategoryIds, selectedSportId, selectedPlatformId, searchQuery]);

  const loadFilters = async () => {
    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');
      
      if (categoriesError) throw categoriesError;
      if (categoriesData) setCategories(categoriesData);
      
      // Load sports
      const { data: sportsData, error: sportsError } = await supabase
        .from('sports')
        .select('id, name')
        .order('name');
      
      if (sportsError) throw sportsError;
      if (sportsData) setSports(sportsData);
      
      // Load platforms
      const { data: platformsData, error: platformsError } = await supabase
        .from('platforms')
        .select('id, name')
        .order('name');
      
      if (platformsError) throw platformsError;
      if (platformsData) setPlatforms(platformsData);
      
    } catch (error) {
      console.error('Error loading filters:', error);
      toast({
        title: "Error loading filters",
        description: "There was a problem loading the filters. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategoryIds(prevSelectedIds => {
      if (prevSelectedIds.includes(categoryId)) {
        return prevSelectedIds.filter(id => id !== categoryId);
      } else {
        return [...prevSelectedIds, categoryId];
      }
    });
  };

  const loadTools = async () => {
    setIsLoading(true);
    
    try {
      // Current week (Monday to Sunday)
      const now = new Date();
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 }); // 1 = Monday
      const currentWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
      
      // Last week
      const lastWeekStart = subWeeks(currentWeekStart, 1);
      const lastWeekEnd = subWeeks(currentWeekEnd, 1);
      
      // Two weeks ago
      const twoWeeksAgoStart = subWeeks(currentWeekStart, 2);
      const twoWeeksAgoEnd = subWeeks(currentWeekEnd, 2);
      
      // Three weeks ago
      const threeWeeksAgoStart = subWeeks(currentWeekStart, 3);
      const threeWeeksAgoEnd = subWeeks(currentWeekEnd, 3);
      
      // Define all time periods to check
      const timePeriods = [
        { 
          period: "This Week", 
          startDate: currentWeekStart, 
          endDate: currentWeekEnd,
          tools: [] as Tool[]
        },
        { 
          period: "Last Week", 
          startDate: lastWeekStart, 
          endDate: lastWeekEnd,
          tools: [] as Tool[]
        },
        { 
          period: "Two Weeks Ago", 
          startDate: twoWeeksAgoStart, 
          endDate: twoWeeksAgoEnd,
          tools: [] as Tool[]
        },
        { 
          period: "Three Weeks Ago", 
          startDate: threeWeeksAgoStart, 
          endDate: threeWeeksAgoEnd,
          tools: [] as Tool[]
        }
      ];
      
      // Execute queries for each time period
      let allPeriodsWithTools = [];
      
      for (const period of timePeriods) {
        // Start building the query
        let query = supabase
          .from('apps')
          .select(`
            id, 
            name, 
            description, 
            visit_url,
            url_slug,
            approved,
            featured,
            logo_url,
            created_at,
            app_categories!inner(category_id, categories(name)), 
            app_platforms!inner(platform_id, platforms(name)), 
            app_sports!inner(sport_id, sports(name))
          `)
          .eq('approved', true)
          .gte('created_at', period.startDate.toISOString())
          .lte('created_at', period.endDate.toISOString());
        
        // Apply search filter if provided
        if (searchQuery.trim()) {
          query = query.ilike('name', `%${searchQuery}%`);
        }
        
        // Apply category filter if selected
        if (selectedCategoryIds.length > 0) {
          query = query.in('app_categories.category_id', selectedCategoryIds);
        }
        
        // Apply sport filter if selected
        if (selectedSportId) {
          query = query.eq('app_sports.sport_id', selectedSportId);
        }
        
        // Apply platform filter if selected
        if (selectedPlatformId) {
          query = query.eq('app_platforms.platform_id', selectedPlatformId);
        }
        
        // Execute the query
        const { data, error } = await query.order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Transform the data into the Tool format
        if (data && data.length > 0) {
          const transformedTools: Tool[] = data.map(app => {
            // Extract unique categories, platforms, and sports
            const categories = Array.from(new Set(
              app.app_categories.map((c: any) => c.categories.name)
            ));
            
            const platforms = Array.from(new Set(
              app.app_platforms.map((p: any) => p.platforms.name)
            ));
            
            const sports = Array.from(new Set(
              app.app_sports.map((s: any) => s.sports.name)
            ));
            
            return {
              id: app.id,
              name: app.name,
              logo: app.logo_url || "https://placehold.co/60x60",
              rating: 4.5, // Default rating
              reviews: 0, // Default reviews count
              bookmarks: 0, // Default bookmarks count
              pricing: "Free Trial", // Default pricing
              description: app.description,
              tags: [], // Could be derived from categories or sports
              category: categories[0] || "", // Using the first category
              sports: sports,
              platforms: platforms,
              featured: app.featured,
              visitUrl: app.visit_url,
              url_slug: app.url_slug,
            };
          });
          
          period.tools = transformedTools;
          allPeriodsWithTools.push(period);
        }
      }
      
      // Only show periods that have tools
      setToolsByWeek(allPeriodsWithTools);
      
    } catch (error) {
      console.error('Error loading tools:', error);
      toast({
        title: "Error loading apps",
        description: "There was a problem loading the apps. Please refresh the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MetaTags
        title="FitnessAppSearch - Discover the Best Fitness Apps"
        description="Find and compare the best fitness apps for your needs. Browse by category, sport, or platform to discover top-rated fitness and workout applications."
        canonicalUrl={window.location.origin}
        imageUrl={`${window.location.origin}/og-image.png`}
      />
      <Navigation />
      <div className="container py-8 flex-grow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Fitness App Search</h1>
            <p className="text-gray-600">Discover the Best Fitness & Sports Applications</p>
          </div>
          <div className="mt-4 md:mt-0">
            <SubmitToolDialog />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="md:col-span-1">
            <div className="bg-white p-4 rounded-lg border space-y-4">
              <div className="mb-4">
                <SearchBar value={searchQuery} onChange={setSearchQuery} />
              </div>
              
              <CategoryFilter 
                categories={categories} 
                selectedCategoryIds={selectedCategoryIds} 
                onSelectCategory={handleSelectCategory} 
              />
              
              <SportsFilter 
                sports={sports} 
                selectedSportId={selectedSportId} 
                onSelectSport={setSelectedSportId} 
              />
              
              <PlatformFilter 
                platforms={platforms} 
                selectedPlatformId={selectedPlatformId} 
                onSelectPlatform={setSelectedPlatformId} 
              />
              
              {(selectedCategoryIds.length > 0 || selectedSportId || selectedPlatformId || searchQuery) && (
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => {
                    setSelectedCategoryIds([]);
                    setSelectedSportId(null);
                    setSelectedPlatformId(null);
                    setSearchQuery("");
                  }}
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          </div>
          
          <div className="md:col-span-3">
            <main>
              {isLoading ? (
                <div className="text-center py-12">
                  <p>Loading apps...</p>
                </div>
              ) : (
                <>
                  {toolsByWeek.length > 0 ? (
                    toolsByWeek.map((periodData, index) => (
                      <div key={index} className="mb-10">
                        <div className="flex items-center mb-4">
                          <h2 className="text-2xl font-semibold">{periodData.period}</h2>
                          <span className="text-sm text-gray-500 ml-2">
                            {format(periodData.startDate, 'MMM d')} - {format(periodData.endDate, 'MMM d')}
                          </span>
                        </div>
                        <div className="space-y-2 bg-white rounded-lg border">
                          {periodData.tools.map((tool) => (
                            <ToolCard key={tool.id} tool={tool} />
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-white rounded-lg border">
                      <p className="text-gray-500">No tools found with the current filters.</p>
                      <div className="mt-4">
                        <Button onClick={() => {
                          setSelectedCategoryIds([]);
                          setSelectedSportId(null);
                          setSelectedPlatformId(null);
                          setSearchQuery("");
                        }}>
                          Clear Filters
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Index;
